/**
 * Secure Route - Protected routing with role-based access control
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useRole } from '../../contexts/RoleContext';

const SecureRoute = ({ 
  children, 
  requireAuth = true,
  role,
  roles,
  permission,
  permissions,
  requireAll = false,
  redirectTo = '/auth',
  fallbackComponent = null
}) => {
  const { isAuthenticated, loading } = useAuth();
  const {
    hasRole,
    hasAnyRole,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions
  } = useRole();
  const location = useLocation();

  // Show loading while checking authentication
  if (loading) {
    return (
      <div className="route-loading">
        <div className="loading-spinner"></div>
        <p>Verifying access...</p>
      </div>
    );
  }

  // Check if authentication is required
  if (requireAuth && !isAuthenticated) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // If authenticated, check role and permission requirements
  if (isAuthenticated) {
    let hasAccess = true;

    // Check single role
    if (role && !hasRole(role)) {
      hasAccess = false;
    }

    // Check multiple roles
    if (roles && !hasAnyRole(roles)) {
      hasAccess = false;
    }

    // Check single permission
    if (permission && !hasPermission(permission)) {
      hasAccess = false;
    }

    // Check multiple permissions
    if (permissions) {
      if (requireAll) {
        if (!hasAllPermissions(permissions)) {
          hasAccess = false;
        }
      } else {
        if (!hasAnyPermission(permissions)) {
          hasAccess = false;
        }
      }
    }

    if (!hasAccess) {
      if (fallbackComponent) {
        return fallbackComponent;
      }
      
      return (
        <div className="access-denied-page">
          <div className="access-denied-content">
            <div className="access-denied-icon">🔒</div>
            <h2>Access Denied</h2>
            <p>You don't have permission to access this page.</p>
            <p>Required access level not met for your current role.</p>
            <button 
              onClick={() => window.history.back()}
              className="back-button"
            >
              Go Back
            </button>
          </div>
        </div>
      );
    }
  }

  return children;
};

// Convenience components for specific roles
export const PlatformAdminRoute = ({ children, ...props }) => (
  <SecureRoute role="platform_admin" {...props}>
    {children}
  </SecureRoute>
);

export const ClubAdminRoute = ({ children, ...props }) => (
  <SecureRoute role="club_admin" {...props}>
    {children}
  </SecureRoute>
);

export const CoachRoute = ({ children, ...props }) => (
  <SecureRoute role="coach" {...props}>
    {children}
  </SecureRoute>
);

export const ParentRoute = ({ children, ...props }) => (
  <SecureRoute role="parent" {...props}>
    {children}
  </SecureRoute>
);

export const BranchAdminRoute = ({ children, ...props }) => (
  <SecureRoute role="branch_admin" {...props}>
    {children}
  </SecureRoute>
);

export const PlayerRoute = ({ children, ...props }) => (
  <SecureRoute role="player" {...props}>
    {children}
  </SecureRoute>
);

// Higher-order routes for administrative access
export const AdminRoute = ({ children, ...props }) => (
  <SecureRoute roles={['platform_admin', 'club_admin']} {...props}>
    {children}
  </SecureRoute>
);

export const StaffRoute = ({ children, ...props }) => (
  <SecureRoute roles={['platform_admin', 'club_admin', 'coach']} {...props}>
    {children}
  </SecureRoute>
);

export default SecureRoute;